import React, { useCallback, useEffect, useRef, useState } from 'react';
import './carousel.css';

const MANUAL_RESUME_DELAY = 3000;

/**
 * Responsive Carousel (no deps)
 * - Auto-play if images >= 2
 * - Touch/drag support
 * - Adaptive height
 * - Keyboard accessible
 * - showControls / showDots toggles for preview use
 */
const Carousel = ({ images = [], alt = '', autoInterval = 3500, showControls = true, showDots = true }) => {
  const list = images.length ? images : ['/assets/hero-illustration.svg'];
  const listLength = list.length;
  const hasLoop = listLength > 1;
  const extendedList = hasLoop ? [list[listLength - 1], ...list, list[0]] : list;
  const initialPosition = hasLoop ? 1 : 0;
  const [position, setPosition] = useState(initialPosition);
  const [isJumping, setIsJumping] = useState(false);
  const trackRef = useRef(null);
  const viewportRef = useRef(null);
  const intervalRef = useRef(null);
  const resumeTimeoutRef = useRef(null);
  const positionRef = useRef(initialPosition);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    const nextPosition = hasLoop ? 1 : 0;
    positionRef.current = nextPosition;
    setPosition(nextPosition);
  }, [hasLoop, listLength]);

  const stopAuto = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const clearResumeTimeout = useCallback(() => {
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
  }, []);

  const step = useCallback((delta) => {
    if (!listLength) return;
    if (hasLoop) {
      const minPosition = 0;
      const maxPosition = listLength + 1;
      setPosition((prev) => {
        let next = prev + delta;
        if (next < minPosition) next = minPosition;
        if (next > maxPosition) next = maxPosition;
        positionRef.current = next;
        return next;
      });
    } else {
      setPosition((prev) => {
        const next = prev + delta;
        const clamped = Math.min(Math.max(next, 0), listLength - 1);
        positionRef.current = clamped;
        return clamped;
      });
    }
  }, [hasLoop, listLength]);

  const startAuto = useCallback(() => {
    if (listLength < 2) return;
    stopAuto();
    intervalRef.current = setInterval(() => {
      step(1);
    }, autoInterval);
  }, [autoInterval, listLength, step, stopAuto]);

  const pause = useCallback(() => {
    clearResumeTimeout();
    stopAuto();
  }, [clearResumeTimeout, stopAuto]);

  const resumeAfterDelay = useCallback((delay = MANUAL_RESUME_DELAY) => {
    if (listLength < 2) return;
    clearResumeTimeout();
    stopAuto();
    resumeTimeoutRef.current = setTimeout(() => {
      resumeTimeoutRef.current = null;
      startAuto();
    }, Math.max(0, delay));
  }, [clearResumeTimeout, listLength, startAuto, stopAuto]);

  const resume = useCallback(() => {
    resumeAfterDelay(0);
  }, [resumeAfterDelay]);

  useEffect(() => {
    if (listLength < 2) return () => undefined;
    startAuto();
    return () => {
      stopAuto();
      clearResumeTimeout();
    };
  }, [listLength, startAuto, stopAuto, clearResumeTimeout]);

  const goToIndex = useCallback((target) => {
    if (target < 0 || target >= listLength) return;
    if (hasLoop) {
      const next = target + 1;
      positionRef.current = next;
      setPosition(next);
    } else {
      positionRef.current = target;
      setPosition(target);
    }
  }, [hasLoop, listLength]);

  const manualAction = useCallback((event, action) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    pause();
    if (typeof action === 'function') action();
    resumeAfterDelay(MANUAL_RESUME_DELAY);
  }, [pause, resumeAfterDelay]);

  const next = useCallback((event) => manualAction(event, () => step(1)), [manualAction, step]);
  const prev = useCallback((event) => manualAction(event, () => step(-1)), [manualAction, step]);
  const go = useCallback((event, i) => {
    manualAction(event, () => goToIndex(i));
  }, [goToIndex, manualAction]);

  const activeIndex = listLength
    ? (hasLoop ? ((position - 1 + listLength) % listLength) : position)
    : 0;

  useEffect(() => {
    if (!hasLoop) return undefined;
    const track = trackRef.current;
    if (!track) return undefined;

    const handleTransitionEnd = () => {
      if (position === 0) {
        setIsJumping(true);
        positionRef.current = listLength;
        setPosition(listLength);
      } else if (position === listLength + 1) {
        setIsJumping(true);
        positionRef.current = 1;
        setPosition(1);
      }
    };

    track.addEventListener('transitionend', handleTransitionEnd);
    return () => {
      track.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [hasLoop, position, listLength]);

  useEffect(() => {
    if (!isJumping) return undefined;
    let frame1;
    let frame2;
    frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => {
        setIsJumping(false);
      });
    });
    return () => {
      if (frame1) cancelAnimationFrame(frame1);
      if (frame2) cancelAnimationFrame(frame2);
    };
  }, [isJumping]);

  useEffect(() => {
    const track = trackRef.current;
    const viewport = viewportRef.current;
    if (!track || !viewport) return undefined;
    let startX = 0;
    let dx = 0;
    let touching = false;

    const onStart = (e) => {
      if (!e.touches || e.touches.length !== 1) return;
      touching = true;
      startX = e.touches[0].clientX;
      dx = 0;
      pause();
      track.style.transition = 'none';
    };

    const onMove = (e) => {
      if (!touching) return;
      dx = e.touches[0].clientX - startX;
      const width = viewport.offsetWidth || 1;
      const base = -positionRef.current * width;
      track.style.transform = `translateX(${base + dx}px)`;
    };

    const onEnd = () => {
      if (!touching) return;
      const width = viewport.offsetWidth || 1;
      track.style.transition = '';
      const currentPosition = positionRef.current;
      const basePosition = hasLoop ? currentPosition : Math.max(0, Math.min(currentPosition, listLength - 1));
      track.style.transform = `translateX(-${basePosition * 100}%)`;
      if (Math.abs(dx) > Math.max(60, width * 0.1)) {
        step(dx < 0 ? 1 : -1);
      }
      touching = false;
      dx = 0;
      resumeAfterDelay(MANUAL_RESUME_DELAY);
    };

    track.addEventListener('touchstart', onStart, { passive: true });
    track.addEventListener('touchmove', onMove, { passive: true });
    track.addEventListener('touchend', onEnd);
    track.addEventListener('touchcancel', onEnd);

    return () => {
      track.removeEventListener('touchstart', onStart);
      track.removeEventListener('touchmove', onMove);
      track.removeEventListener('touchend', onEnd);
      track.removeEventListener('touchcancel', onEnd);
      track.style.transition = '';
      const currentPosition = positionRef.current;
      const basePosition = hasLoop ? currentPosition : Math.max(0, Math.min(currentPosition, listLength - 1));
      track.style.transform = `translateX(-${basePosition * 100}%)`;
    };
  }, [pause, resumeAfterDelay, step, hasLoop, listLength]);

  const trackStyle = {
    transform: `translateX(-${(hasLoop ? position : activeIndex) * 100}%)`,
    transition: isJumping ? 'none' : undefined,
  };

  return (
    <div className="cr" onMouseEnter={pause} onMouseLeave={resume}>
      <div ref={viewportRef} className="cr-viewport">
        <div ref={trackRef} className="cr-track" style={trackStyle}>
          {extendedList.map((src, i) => (
            <div key={`slide-${i}`} className="cr-slide">
              <img src={src} alt={alt} />
            </div>
          ))}
        </div>
      </div>

      {showControls && listLength > 1 && (
        <>
          <button type="button" className="cr-btn prev" aria-label="Previous" onClick={prev}>‹</button>
          <button type="button" className="cr-btn next" aria-label="Next" onClick={next}>›</button>
        </>
      )}

      {showDots && listLength > 1 && (
        <div className="cr-dots" role="tablist">
          {list.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === activeIndex}
              className={`dot ${i === activeIndex ? 'on' : ''}`}
              onClick={(event) => go(event, i)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;
